import angular from 'angular'
import { ApiCustomer } from '@vend/types'
import type { ModalFactory } from '../vend-ui/types'

import editCustomerTemplate from '../components/react/business-components/EditCustomerModal/edit-customer-modal.html'
import { EditCustomerModalResult } from '../components/react/business-components/EditCustomerModal/edit-customer-modal.controller'
import { detectCustomerFieldFromQuery } from '../functions/customer'

/**
 * Provides customer related modals e.g. to allow the user to create/edit/view customers.
 *
 * @module customerModals
 */
angular
  .module('webregister.modals')
  .factory('customerModals', function (Modal: ModalFactory) {
    /**
     * Provides customer related modals e.g. to allow the user to create/edit/view customers.
     *
     * @class CustomerLightboxes
     * @static
     */
    return {
      // ============================== Public API ============================== //

      /**
       * Responsible for showing the edit customer modal instance.
       *
       * @method updateCustomer
       *
       * @param  customer {Customer}
       *         An existing customer instance
       *
       * @return {Promise<Customer>} A promise resolved with the updated customer details.
       */
      updateCustomer(customer: ApiCustomer) {
        return this._openEditCustomer({
          customerId: customer.id,
        })
      },

      /**
       * Responsible for showing the create new customer modal instance.
       *
       * @method createCustomer
       *
       * @param  [options] {Object}
       *
       *         @param [options.query] {String}
       *                The search query accompanying the create customer method.
       *
       * @return {Promise<Customer>} A promise resolved with the newly created customer details.
       */
      createCustomer(options?: { query?: string }) {
        return this._openEditCustomer(options)
      },

      // ============================== Private API ============================== //

      /**
       * Responsible for opening the edit customer modal. A shared modal, capable of either editing an existing customer
       * or creating a new one.
       *
       * @private
       * @method _openEditCustomer
       *
       * @param  [options] {Object}
       *
       *         @param [options.customerId] {String}
       *                The id of the customer to edit, or undefined
       *
       *         @param [options.query] {String}
       *                The query to prepopulate the form with
       *
       * @return {Promise<Customer>} A promised resolved with either the newly created customer or the updated customer
       *                             instance.
       */
      _openEditCustomer(
        options: {
          customerId?: string
          query?: string
        } = {}
      ) {
        const {
          email,
          mobile,
          firstName,
          lastName,
        } = detectCustomerFieldFromQuery(options.query || '')

        const customer = {
          id: options.customerId,
          firstName,
          lastName,
          contact: {
            email,
            mobile,
          },
        }

        return Modal({
          controller: 'editCustomerModalController',
          controllerAs: '$ctrl',
          template: editCustomerTemplate,
          customer,
        }).then((modal: $TSFixMe) => {
          return modal.closed.then(
            (result: EditCustomerModalResult) => result.customer
          )
        })
      },
    }
  })
